/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

export interface PixelTemplateSnippetInput {
  id?: string;
  name?: string;
  type?: 'impression' | 'tracking' | 'custom';
  urlTemplate?: string;
}

export interface PixelTemplateSnippetOptions {
  proxyBaseUrl?: string;
}

export const DEFAULT_PROXY_PIXEL_URL_TEMPLATE =
  '{{PROXY_BASE_URL}}/pixel?pid=pricing&sid={sessionId}&event=impression&cpm={bid}&cb={cb}';

export function resolveProxyBaseUrlPlaceholder(value: string | undefined, proxyBaseUrl?: string): string {
  const template = value ?? '';
  const resolvedBase = proxyBaseUrl?.trim();
  if (!resolvedBase) {
    return template;
  }

  return template.replaceAll('{{PROXY_BASE_URL}}', resolvedBase.replace(/\/$/, ''));
}

/**
 * Generate a copy-ready pixel snippet for ad-server configuration.
 */
export function generatePixelTemplateSnippet(
  pixel: PixelTemplateSnippetInput,
  options?: PixelTemplateSnippetOptions,
): string {
  const base = pixel.urlTemplate || '';
  const configuredProxyBase = options?.proxyBaseUrl?.trim();
  const proxyBase = configuredProxyBase ? configuredProxyBase.replace(/\/$/, '') : '{{PROXY_BASE_URL}}';

  const resolvedBase = resolveProxyBaseUrlPlaceholder(base, configuredProxyBase);

  const proxyBaseComment = configuredProxyBase
    ? `<!-- Proxy base URL auto-filled from system defaults: ${proxyBase} -->\n`
    : '<!-- Replace {{PROXY_BASE_URL}} with your proxy service hostname -->\n';

  return (
    `<!-- Pixel template for "${pixel.name ?? 'pixel'}" -->\n` +
    proxyBaseComment +
    `<!-- Embed in VAST <Impression> or <Tracking> element: -->\n` +
    `<![CDATA[${proxyBase}/pixel?pid=${pixel.id ?? 'PIXEL_ID'}&sid=%%SESSION_ID%%&event=${pixel.type ?? 'impression'}]]>\n\n` +
    `<!-- Original URL template (for reference):\n${resolvedBase}\n-->`
  );
}
